// src/features/merchantConfig/components/CategorySidebar.jsx
//
// 12-item sticky list with humanized names + status dots (UI prompt §4).
//
// Accessibility:
//   * Each item is a real button-shaped link with `aria-current="page"` on
//     the selected row.
//   * Status dots have aria-labels AND a glyph inside (✓ / - / !) so users
//     don't depend on color (WCAG, UI prompt §9).
//   * Roving keyboard navigation (↑/↓) is handled by react-router-dom <NavLink>'s
//     natural tab order plus an `onKeyDown` for arrow navigation.
//
// Mobile: <768px collapses into a horizontal scroll pill bar.

import React, { useRef, useCallback } from "react";
import { Check, Minus, AlertTriangle, Loader2 } from "lucide-react";
import { MERCHANT_CONFIG_CATALOG } from "../lib/configKeyCatalog";

/* ---------- Status dot ---------- */

function StatusDot({ status }) {
  if (status === "loading") {
    return (
      <span
        className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-gray-100 text-gray-400"
        aria-label="Loading"
      >
        <Loader2 className="h-2.5 w-2.5 animate-spin" aria-hidden="true" />
      </span>
    );
  }
  if (status === "provisioned") {
    return (
      <span
        className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-green-100 text-green-700"
        aria-label="Provisioned"
        title="Provisioned"
      >
        <Check className="h-2.5 w-2.5" strokeWidth={3} aria-hidden="true" />
      </span>
    );
  }
  if (status === "notProvisioned") {
    return (
      <span
        className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-gray-200 text-gray-500"
        aria-label="Not provisioned"
        title="Not provisioned"
      >
        <Minus className="h-2.5 w-2.5" strokeWidth={3} aria-hidden="true" />
      </span>
    );
  }
  return (
    <span
      className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-100 text-red-700"
      aria-label="Error loading category"
      title="Error loading category"
    >
      <AlertTriangle className="h-2.5 w-2.5" strokeWidth={2.5} aria-hidden="true" />
    </span>
  );
}

/* ---------- Mobile pill bar ---------- */

function MobilePillBar({ probes, selectedSlug, onSelect }) {
  const probesBySlug = Object.fromEntries(probes.map((p) => [p.slug, p]));
  return (
    <nav
      aria-label="Configuration categories"
      className="overflow-x-auto border-b border-gray-200 bg-white py-2"
    >
      <ul className="flex min-w-max gap-2 px-3">
        {MERCHANT_CONFIG_CATALOG.map((entry) => {
          const probe = probesBySlug[entry.slug] || { status: "loading" };
          const active = selectedSlug === entry.slug;
          return (
            <li key={entry.slug}>
              <button
                type="button"
                onClick={() => onSelect(entry.slug)}
                aria-current={active ? "page" : undefined}
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm transition ${
                  active
                    ? "border-blue-600 bg-blue-600 text-white"
                    : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                }`}
              >
                <StatusDot status={probe.status} />
                <span>{entry.displayName}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/* ---------- Desktop sticky sidebar ---------- */

/**
 * @param {{
 *   probes: Array<{ key: string, slug: string, displayName: string, status: string }>,
 *   selectedSlug: string | null,
 *   onSelect: (slug: string) => void,
 * }} props
 */
export default function CategorySidebar({ probes, selectedSlug, onSelect }) {
  const probesBySlug = Object.fromEntries(probes.map((p) => [p.slug, p]));
  const listRef = useRef(null);

  // Roving ↑/↓ keyboard navigation between rows.
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      e.preventDefault();
      const buttons = listRef.current?.querySelectorAll(
        'button[data-sidebar-item="true"]'
      );
      if (!buttons || buttons.length === 0) return;
      const active = document.activeElement;
      const currentIndex = Array.from(buttons).indexOf(active);
      const nextIndex =
        e.key === "ArrowDown"
          ? (currentIndex + 1) % buttons.length
          : (currentIndex - 1 + buttons.length) % buttons.length;
      buttons[nextIndex]?.focus();
    },
    []
  );

  return (
    <>
      {/* Mobile (<md) */}
      <div className="md:hidden">
        <MobilePillBar
          probes={probes}
          selectedSlug={selectedSlug}
          onSelect={onSelect}
        />
      </div>

      {/* Desktop (md+) */}
      <aside
        aria-label="Configuration categories"
        className="hidden md:block md:sticky md:top-4 md:h-fit md:w-64 md:shrink-0"
      >
        <ul
          ref={listRef}
          className="space-y-1"
          onKeyDown={handleKeyDown}
        >
          {MERCHANT_CONFIG_CATALOG.map((entry) => {
            const probe = probesBySlug[entry.slug] || { status: "loading" };
            const active = selectedSlug === entry.slug;
            const Icon = entry.icon;
            return (
              <li key={entry.slug}>
                <button
                  type="button"
                  data-sidebar-item="true"
                  aria-current={active ? "page" : undefined}
                  onClick={() => onSelect(entry.slug)}
                  className={`group flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                    active
                      ? "border-blue-200 bg-blue-50 text-blue-900 shadow-sm"
                      : "border-transparent bg-white text-gray-700 hover:border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <StatusDot status={probe.status} />
                  {Icon ? (
                    <Icon
                      className={`h-4 w-4 shrink-0 ${
                        active ? "text-blue-700" : "text-gray-400 group-hover:text-gray-600"
                      }`}
                      aria-hidden="true"
                    />
                  ) : null}
                  <span className="flex-1 truncate font-medium">
                    {entry.displayName}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </aside>
    </>
  );
}
